import React from 'react';
import { useAuth } from '../../contexts/Auth/authContext';
import { useI18n } from '../../contexts/I18nContext';
import { useUiVisibility } from '../../contexts/UiVisibilityContext';
import { useLayout } from '../../contexts/LayoutContext';
import { ApiStatusIndicator } from '../common/ApiStatusIndicator';
import { HeaderLanguageSelector } from '../common/HeaderLanguageSelector';
import { DebugMenu } from '../debug/DebugMenu';
import { ThemeToggleButton } from './ThemeToggleButton';
import { UserMenu } from './UserMenu';
import { TransferBell } from './TransferBell';
import { GlobalSearch } from './GlobalSearch';
import { MenuIcon } from './icons';
import config from '../../config/env';
import './Navbar.css';

const MOBILE_QUERY = '(max-width: 768px)';

// Reject logoUrl values whose scheme could embed mixed-content warnings or
// non-image data URIs. Accepts http(s), root-relative paths, and data:image/*.
export const sanitiseLogoUrl = (raw: string | undefined): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/')) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^data:image\/(png|jpe?g|gif|svg\+xml|webp);/i.test(trimmed)) return trimmed;
  return null;
};

/**
 * The top bar: identity on the left, session controls on the right.
 *
 * Everything that concerns the *person* rather than the *data* lives here —
 * theme, language, the account menu — so the sidebar can be navigation and
 * nothing else.
 */
export const Navbar: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useI18n();
  const { showApiStatus, showLanguageSelector, ready } = useUiVisibility();
  const { toggleSidebar, mobileNavOpen, setMobileNavOpen } = useLayout();

  // The sign-in screen carries its own identity and its own language
  // switcher, so a second bar above it would only repeat both.
  if (!isAuthenticated && !loading) {
    return null;
  }

  const logoUrl = sanitiseLogoUrl(config.ui.logoUrl);

  // Shown on narrow screens only (see Navbar.css), where it opens the
  // drawer. On a desktop the sidebar carries its own collapse control, so
  // this stays a fallback for the odd case where the CSS breakpoint and the
  // JS one disagree.
  const handleMenu = () => {
    const isMobile = typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches;
    if (isMobile) {
      setMobileNavOpen(!mobileNavOpen);
    } else {
      toggleSidebar();
    }
  };

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button
          type="button"
          className="topbar-icon-btn topbar-menu-btn"
          onClick={handleMenu}
          aria-label={t('nav.openMenu')}
          aria-expanded={mobileNavOpen}
          data-testid="btn-menu"
        >
          <MenuIcon />
        </button>
        {/* The ANEP mark is itself magenta-to-indigo: on the brand gradient it
            would vanish. A white tile gives it ground of its own. */}
        {logoUrl && (
          <span className="topbar-logo-tile">
            <img src={logoUrl} alt={config.ui.companyName} className="topbar-logo" />
          </span>
        )}
        <span className="topbar-product">{config.ui.title}</span>
      </div>

      {/* Only once the session is established: anything that calls the API
          from the bar before the token exists gets a 401, and the client
          answers a 401 it cannot refresh by wiping the session and reloading. */}
      <div className="topbar-center">
        {isAuthenticated && <GlobalSearch />}
      </div>

      <div className="topbar-right">
        {/* Admin-hideable chrome waits for the visibility settings so nothing
            flashes on first paint only to disappear when the answer arrives. */}
        {ready && showApiStatus && <ApiStatusIndicator />}
        {ready && <DebugMenu />}
        {ready && showLanguageSelector && <HeaderLanguageSelector />}
        <span className="topbar-divider" aria-hidden="true" />
        <TransferBell />
        <ThemeToggleButton />
        <UserMenu />
      </div>
    </header>
  );
};
